import "./style.scss";

import { ChipBackgroundColor, ChipSize } from "@amzn/stencil-react-components/chip";
import Chip from "@amzn/stencil-react-components/dist/submodules/chip/Chip";
import { PopoverPosition } from "@amzn/stencil-react-components/dist/submodules/popover";
import { Col, Flex, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { Link, LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import IconCheckCircleFill from "@amzn/stencil-react-icons/icons/icon-check-circle-fill";
import IconInformation from "@amzn/stencil-react-icons/icons/icon-information";
import IconMail from "@amzn/stencil-react-icons/icons/icon-mail";
import { IconSize } from "@amzn/stencil-react-icons/icons/interfaces";
import Button, { ButtonVariant } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { BaseClickPayload, Bucket, trackResultClicked } from "common/helpers/search-analytics";
import { TrafficFrom } from "common/helpers/traffic-from";
import { SearchResultProps } from "common/types";
import { ItemStatus } from "common/types/catalog-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { useProductFeedback } from "hooks/useProductFeedback";
import NoLogoImage from "img/no_logo_image.png";
import { UserPermissions } from "lib/services";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import React, { FC, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { FoundationalChip } from "../FoundationalChip";
import { ListActions } from "../ListActions/ListActions";
import { ProductReviewsFlyout } from "../ProductReviews/ProductReviewsFlyout";
import { StarAverageRating, StarAverageRatingSize, StarDetailedRating, StarDetailedRatingSize } from "../Ratings";
import { SubmitReviewForm } from "../SubmitReviewForm";
import { getEtaRange } from "./helpers";

// String paths for i18n
const STRING_PATH = "searchResults.searchResult";

// Tag constants
const VALUE_PICK_TAG = "Foundational";

// Color constants
const NEUTRAL_GRAY_COLOR = "var(--neutral-70, #56616C)";

// Size constants
const PRODUCT_IMAGE_SIZE = "50px";
const PRODUCT_IMAGE_CONTAINER_SIZE = "74px";
const PRODUCT_IMAGE_PADDING = "12px";

// Chip color mapping
const chipColorMap: Record<string, ChipBackgroundColor> = {
  os: ChipBackgroundColor.Default,
  availability: ChipBackgroundColor.Yellow,
  availabilityGreen: ChipBackgroundColor.Green,
  approval: ChipBackgroundColor.Pink,
  recommendations: ChipBackgroundColor.Blue,
};

export const SearchResultV2: FC<SearchResultProps> = (props: SearchResultProps) => {
  const history = useHistory();
  let detailsUrl = "#";
  const tags: string[] = props.tags ?? [];
  const recommendations = props.recommendations ?? "";
  const approval: string | undefined = props.approval ?? "";
  const IconInformationWithTooltip = withTooltip()(IconInformation);

  // Check if software reviews are enabled
  const areSoftwareReviewsEnabled = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_SOFTWARE_REVIEWS);

  // State for reviews flyouts
  const [openCheckReviewsFlyout, setOpenCheckReviewsFlyout] = useState(false);
  const [openWriteReviewFlyout, setOpenWriteReviewFlyout] = useState(false);
  const [rating, setRating] = useState(props.averageRating ?? 0);

  // Product feedback hook for review submission
  const {
    isLoading: feedbackLoading,
    doPostFeedback,
    setItemFeedbackStats,
  } = useProductFeedback({
    domain: "software",
  });

  // Since some catalog items will not have a strain value, parsing the taxonomy
  // could throw an error, handling this with a try/catch in the meantime
  try {
    detailsUrl = `items/${ItemTaxonomyId.Parse(props.taxonomyId!).getUrlSuffix()}?from=${TrafficFrom.Search}`;
  } catch (error) {
    // TODO: report this error to cloudwatch. (To be resolved by ITMP-344)
  }

  const trackUxmSearchResultClick = () => {
    const payload: BaseClickPayload = {
      taxonomyId: props.taxonomyId,
      name: props.name,
      rank: props.rank,
      score: props.score,
      queryId: props.searchMetadata?.queryId ?? "-",
      visibleResultsCount: props.searchMetadata?.visibleResultCount,
      totalResultsCount: props.searchMetadata?.resultCount,
      bucket: tags.includes(VALUE_PICK_TAG) ? Bucket.Foundational : Bucket.Organic,
    };

    trackResultClicked(payload);
  };

  const onSearchResultClick = (event: any) => {
    trackUxmSearchResultClick();

    if (detailsUrl !== "#") {
      if (event.ctrlKey || event.metaKey) return true; // allows a new tab if ctrl + click and cmd + click

      history.push(detailsUrl);
      event.preventDefault();
    }
  };

  const getValuePickChip = () => {
    return <FoundationalChip />;
  };

  const getProductLabelChips = (labels: string[], type: string, key: string, icon?: React.ReactNode): JSX.Element[] => {
    const labelChips: JSX.Element[] = [];
    const color = chipColorMap[type] ?? ChipBackgroundColor.Default;

    labels.forEach((label) => {
      labelChips.push(
        <Chip
          key={`${label}-${key}`}
          backgroundColor={color}
          size={ChipSize.ExtraSmall}
          dataTestId={`catalog-product-label-${type}`}
        >
          <Row alignItems="center" gridGap="4px">
            {label === getString(`${STRING_PATH}.usedByTeam`) && (
              <IconInformationWithTooltip
                title="Show tooltip"
                tooltipText={getString(`${STRING_PATH}.usedByTeamTooltip`)}
                size={IconSize.ExtraSmall}
              />
            )}
            {label === getString(`${STRING_PATH}.usedByOrg`) && (
              <IconInformationWithTooltip
                title="Show tooltip"
                tooltipText={getString(`${STRING_PATH}.usedByOrgTooltip`)}
                size={IconSize.ExtraSmall}
              />
            )}
            {icon}
            <Text>{label}</Text>
          </Row>
        </Chip>
      );
    });
    return labelChips;
  };

  const renderProductInfo = () => {
    return (
      <View flex={1} justifyContent="flex-start" dataTestId="catalog-product-info" padding={{ top: "S200" }}>
        <Row gridGap={"S300"}>
          {/* Logo image */}
          <View
            display="flex"
            height={PRODUCT_IMAGE_CONTAINER_SIZE}
            width={PRODUCT_IMAGE_CONTAINER_SIZE}
            padding={PRODUCT_IMAGE_PADDING}
            alignItems="center"
            justifyContent="center"
            className="search-result-img-wrapper"
          >
            <img
              alt={props.name}
              src={props.logoUrl.trim() !== "" ? props.logoUrl : NoLogoImage}
              data-testid="catalog-item-url"
              data-test-id="catalog-item-url"
              data-details-logo="details-logo"
              style={{
                width: PRODUCT_IMAGE_SIZE,
                height: PRODUCT_IMAGE_SIZE,
                objectFit: "contain",
              }}
            />
          </View>

          {/* Title and description */}
          <Col gridGap={"S200"} flex={1}>
            <Row alignItems="center" gridGap={"S200"} dataTestId="catalog-product-title" flexWrap="wrap">
              <Text fontSize="T200" fontWeight="bold" color="neutral90" lineHeight="24px" hyphens="initial">
                {props.name}
              </Text>
              {tags.includes(VALUE_PICK_TAG) && getValuePickChip()}
              {/* TODO: Uncomment when backend support is ready
              {props.views && (
                <Row alignItems="center" gridGap={"4px"}>
                  <IconShow size={IconSize.ExtraSmall} color={NEUTRAL_GRAY_COLOR} />
                  <Text fontSize="T100" color="neutral70">
                    {props.views} {getString(`${STRING_PATH}.views`)}
                  </Text>
                </Row>
              )}
              */}
            </Row>
            <Text
              fontSize="T100"
              fontWeight="regular"
              color="neutral90"
              lineHeight="24px"
              className="product-short-description"
              dataTestId="catalog-product-short-description"
            >
              {props.shortDescription}
            </Text>
          </Col>
        </Row>
      </View>
    );
  };

  const renderPrice = () => {
    const hasRatings = props.averageRating && props.averageRating > 0;

    // "Check reviews" button
    const buttonCheckReviews = (
      <Button
        variant={ButtonVariant.Tertiary}
        onClick={() => setOpenCheckReviewsFlyout(true)}
        data-test-id={"check-reviews-button"}
        touchpoint="search-result-check-reviews"
      >
        {getString("productReviews.checkReviews")}
      </Button>
    );

    const popoverContent = hasRatings ? (
      <StarDetailedRating
        size={StarDetailedRatingSize.Small}
        averageRating={props.averageRating}
        voteNumber={props.numberOfRatings}
        ratingCounts={props.ratingCounts}
        button={buttonCheckReviews}
      />
    ) : undefined;

    return (
      <View dataTestId="catalog-product-price-section" padding={{ top: "S200" }} style={{ minWidth: "200px" }}>
        {/* Show ratings only if software reviews are enabled */}
        {areSoftwareReviewsEnabled && (
          <View
            margin={["0", "0", "8px", "0"]}
            onClick={(e) => {
              // Stop propagation to prevent navigation when clicking on ratings
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <StarAverageRating
              size={StarAverageRatingSize.Small}
              averageRating={hasRatings ? props.averageRating : undefined}
              popoverContent={popoverContent}
              popoverPosition={PopoverPosition.RightCenter}
            />
          </View>
        )}
        {props.price && parseFloat(props.price) > 0 ? (
          <Text>{`$${parseFloat(props.price).toFixed(2)}`}</Text>
        ) : (
          <Row gridGap={"S100"} alignItems="center">
            <Text>
              {tags.includes(VALUE_PICK_TAG)
                ? getString(`${STRING_PATH}.noAdditionalCost`)
                : getString(`${STRING_PATH}.noCost`)}
            </Text>
            <IconInformationWithTooltip
              title="Show tooltip"
              tooltipText={getString(`${STRING_PATH}.noCostTooltip`)}
              size={IconSize.ExtraSmall}
            />
          </Row>
        )}
      </View>
    );
  };

  const renderProductLabels = () => {
    let labelChips: JSX.Element[] = [];

    // ETA / availability tags
    const isSecurityReviewRequired = props.status?.toLocaleLowerCase() === ItemStatus.unsanctioned;

    const isImmediatelyAvailable = props.hasStock === true || !props.eta || props.eta === 0;

    if (isSecurityReviewRequired) {
      // Requires security review — replace availability chip with a dedicated label
      const securityReviewString = getString(`${STRING_PATH}.securityReviewRequired`);
      labelChips = labelChips.concat(getProductLabelChips([securityReviewString], "approval", props.name));
    } else if (isImmediatelyAvailable) {
      // Immediately available
      const etaString = getString(`${STRING_PATH}.noEta`);
      labelChips = labelChips.concat(
        getProductLabelChips(
          [etaString],
          "availabilityGreen",
          props.name,
          <IconCheckCircleFill size={IconSize.ExtraSmall} color="currentColor" />
        )
      );
    } else {
      // Delivered in X-X days
      const etaString = getString(`${STRING_PATH}.deliveredIn`, { eta: getEtaRange(props.eta!) });
      labelChips = labelChips.concat(
        getProductLabelChips(
          [etaString],
          "availability",
          props.name,
          <IconMail size={IconSize.ExtraSmall} color="currentColor" />
        )
      );
    }

    // Other labels
    if (props.os && props.os.length > 0) {
      labelChips = labelChips.concat(getProductLabelChips(props.os, "os", props.name));
    }
    if (approval && approval.length > 0) {
      labelChips = labelChips.concat(getProductLabelChips([approval], "approval", props.name));
    }
    if (recommendations && recommendations.length > 0) {
      const updatedRecommendations = recommendations.replace(
        getString(`${STRING_PATH}.usedInRole`),
        getString(`${STRING_PATH}.usedBySimilarRole`)
      );
      labelChips = labelChips.concat(getProductLabelChips([updatedRecommendations], "recommendations", props.name));
    }

    return (
      <View
        flex={1}
        dataTestId="catalog-product-labels-section"
        padding={{ top: "S200" }}
        margin={["0", "0", "0", "48px"]}
      >
        <Flex flexDirection={"center"} flexWrap={"wrap"} gridGap={"S200"} justifyContent="flex-start">
          {labelChips}
        </Flex>
      </View>
    );
  };

  const memoizedProductLabels = useMemo(
    () => renderProductLabels(),
    [props.os, props.approval, props.recommendations, props.eta, props.hasStock, props.status]
  );

  return (
    <>
      <View margin={["32px", "0"]} data-cy="catalog-product-result" dataTestId="catalog-product-result" padding="S300">
        <Link href={detailsUrl} onClick={onSearchResultClick} textDecoration={LinkTextDecorationOptions.None}>
          <Row gridGap="48px" justifyContent="space-between">
            {renderProductInfo()}
            {renderPrice()}
            {memoizedProductLabels}
            {UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS) && props.lists && (
              <ListActions
                taxonomyId={props.taxonomyId}
                lists={props.lists}
                allowedDomain="software"
                toolName={props.name}
              />
            )}
          </Row>
        </Link>
      </View>
      <Hr />
      {/* Reviews flyout - only show if software reviews are enabled */}
      {areSoftwareReviewsEnabled && openCheckReviewsFlyout && (
        <ProductReviewsFlyout
          productName={props.name}
          titleText={getString("productReviews.checkReviews")}
          isOpen={openCheckReviewsFlyout}
          setIsOpen={setOpenCheckReviewsFlyout}
          setOpenWriteReviewFlyout={setOpenWriteReviewFlyout}
          feedback={{
            averageRating: props.averageRating ?? 0,
            numberOfRatings: props.numberOfRatings ?? 0,
            numberOfFeedback: props.numberOfRatings ?? 0,
            ratingCounts: props.ratingCounts ?? { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
          }}
          taxonomyId={ItemTaxonomyId.Parse(props.taxonomyId)}
          domain="software"
        />
      )}
      {/* Submit review flyout - only show if software reviews are enabled */}
      {areSoftwareReviewsEnabled && openWriteReviewFlyout && (
        <SubmitReviewForm
          productName={props.name}
          titleText={"Submit review"}
          isOpen={openWriteReviewFlyout}
          setIsOpen={setOpenWriteReviewFlyout}
          previousRating={rating}
          setPreviousRating={setRating}
          isLoading={feedbackLoading}
          doPostFeedback={doPostFeedback}
          setItemFeedback={setItemFeedbackStats}
          taxonomyId={ItemTaxonomyId.Parse(props.taxonomyId)}
          domain="software"
        />
      )}
    </>
  );
};
